import React, { useState, useEffect, useMemo } from 'react';
import { CurrencyEuroIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { GasInvoice } from '../types/gas';
import { gasInvoiceService } from '../services/gasService';
import CommonPageHeader from '../components/common/CommonPageHeader';
import LoadingScreen from '../components/common/LoadingScreen';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';

const GasInvoices: React.FC = () => {
  const { t } = useLanguage();
  const [invoices, setInvoices] = useState<GasInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const data = await gasInvoiceService.getAllGasInvoices();
      setInvoices(data);
    } catch (error) {
      console.error('Error loading gas invoices:', error);
      toast.error(t('gas.messages.loadError', { name: t('gas.sections.invoices') }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, []);

  const filteredInvoices = useMemo(() => {
    if (!searchTerm.trim()) return invoices;
    const term = searchTerm.toLowerCase();
    return invoices.filter(invoice =>
      invoice.invoiceNumber?.toLowerCase().includes(term) ||
      invoice.contractId?.toLowerCase().includes(term) ||
      invoice.billingPeriodStart?.includes(term) ||
      invoice.billingPeriodEnd?.includes(term) ||
      invoice.currency.toLowerCase().includes(term)
    );
  }, [invoices, searchTerm]);

  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentInvoices = filteredInvoices.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Vázlat';
      case 'issued': return 'Kiállítva';
      case 'paid': return 'Fizetve';
      case 'cancelled': return 'Sztornó';
      default: return status;
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="space-y-4">
      <CommonPageHeader
        title={t('gas.sections.invoices')}
        subtitle={t('gas.list.subtitle', { count: invoices.length })}
        icon={CurrencyEuroIcon}
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('gas.list.search.invoices')}
          />
        </div>
        <button
          onClick={loadInvoices}
          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4 mr-2" />
          {t('gas.list.refresh')}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.invoice.invoiceNumber')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.invoice.period')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.invoice.gasDays')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.invoice.totalNet')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.invoice.totalVat')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.invoice.totalGross')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.invoice.status')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentInvoices.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                    {t('gas.list.noResults')}
                  </td>
                </tr>
              ) : (
                currentInvoices.map(invoice => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-medium text-gray-900">{invoice.invoiceNumber || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-700">{invoice.billingPeriodStart} → {invoice.billingPeriodEnd}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{invoice.gasDays || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{invoice.totalNet.toLocaleString('hu-HU')} {invoice.currency}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{invoice.totalVat.toLocaleString('hu-HU')} {invoice.currency}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">{invoice.totalGross.toLocaleString('hu-HU')} {invoice.currency}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        invoice.status === 'paid'
                          ? 'bg-green-100 text-green-700'
                          : invoice.status === 'issued'
                            ? 'bg-blue-100 text-blue-700'
                            : invoice.status === 'draft'
                              ? 'bg-gray-100 text-gray-700'
                              : 'bg-red-100 text-red-700'
                      }`}>
                        {getStatusLabel(invoice.status)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={filteredInvoices.length}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        indexOfFirstItem={indexOfFirstItem}
        indexOfLastItem={indexOfLastItem}
      />
    </div>
  );
};

export default GasInvoices;
